/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import { type ReactNode, useEffect, useMemo, useState } from 'react';

import { jsx } from '@compiled/react';
import { ErrorBoundary } from 'react-error-boundary';
import { useIntl } from 'react-intl-next';

import { NavigationAnalyticsContext } from '@atlaskit/analytics-namespaced-context';
import FeatureGates from '@atlaskit/feature-gate-js-client';
import { FlagsProvider } from '@atlaskit/flag';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	BFFConsumerEnum,
	BFFProvider,
	RequestStatus,
	useFetchUser,
	type User,
	UserContext,
	useUCSuggestions,
	useUserContext,
} from '@atlassian/navigation-bff-data-provider';
import { LOOM_ATI } from '@atlassian/universal-create-common';

import { REQUIRED_USER_ITEMS } from '../../../constants';
import { useMenuControl } from '../../../context';
import {
	useSuggestionExcludeAtis,
	useSuggestionNativeAtis,
	useSuggestionPreferredAtis,
} from '../../../hooks';
import { useLoomVideoConfig } from '../SuggestionItem/primitives/LoomItem/hooks/use-loom-video-config';
import { convertSourceProductToProductType } from '../SuggestionItem/primitives/LoomItem/LoomVideoItem';

import { usePrefetchV2 } from './hooks/usePrefetchV2';

function PrefetchWrapper({ children }: { children: ReactNode }) {
	const crossAppCreationEnabled = fg('navx_universal_create_cross_app_creation_enabled');
	const { product, suggestionConfig, subProduct, crossAppCreationConfig, sections } =
		useMenuControl();

	// Determine if prefetch is enabled based on suggestionConfig.prefetch
	const isPrefetchEnabled = suggestionConfig.enabled && suggestionConfig.prefetch === true;
	const { locale } = useIntl();
	// This gets around TypeScript errors where it doesn't understand that we expect suggestionConfig to be
	// of type SuggestionConfigEnabled where we check for and use onResultChange, never SuggestionConfigDisabled
	const onResultChange = suggestionConfig.enabled ? suggestionConfig?.onResultChange : undefined;

	// Use the shared hooks for ATIs logic
	const nativeAtis = useSuggestionNativeAtis();
	const preferredAtis = useSuggestionPreferredAtis();
	const excludeAtis = useSuggestionExcludeAtis();

	const { status, data, isLoading, hasError, fetchUCSuggestions } = useUCSuggestions({
		cloudId: suggestionConfig.enabled ? suggestionConfig.cloudId : '',
		orgId: suggestionConfig.enabled ? suggestionConfig.orgId : '',
		userId: suggestionConfig.enabled ? suggestionConfig.accountId : '',
		product,
		locale,
		subProduct: suggestionConfig.enabled ? subProduct : undefined,
		nativeAtis,
		preferredAtis,
		excludeAtis,
	});

	const hasLoomEnabled =
		!isLoading && !hasError && data?.items?.some((item) => item.ati === LOOM_ATI);

	const skipInitialisation = !isPrefetchEnabled || !crossAppCreationEnabled || !hasLoomEnabled;

	const shouldFetchSuggestions =
		crossAppCreationEnabled && isPrefetchEnabled && suggestionConfig.enabled;

	// V2 prefetch logic for cross-app creation
	const { shouldFetchUser: shouldFetchUserV2 } = usePrefetchV2({
		crossAppCreationConfig,
		product,
		subProduct,
		sections,
	});

	const { user: currentUser } = useUserContext();
	const { fetchUser } = useFetchUser();

	const [user, setUser] = useState<User | undefined>(() => (currentUser ? currentUser : undefined));

	useLoomVideoConfig({
		cloudId: suggestionConfig.enabled ? suggestionConfig.cloudId : '',
		productName: convertSourceProductToProductType(product),
		environment: (suggestionConfig.enabled && suggestionConfig?.environment) || 'prod',
		onItemClick: suggestionConfig.enabled ? suggestionConfig.onItemClick : undefined,
		skipInitialisation,
	});

	// Trigger fetch when environment changes
	useEffect(
		function fetchUCSuggestionsForLoomEnabledApps() {
			if (shouldFetchSuggestions) {
				fetchUCSuggestions();
			}
		},
		[shouldFetchSuggestions, fetchUCSuggestions],
	);

	// Memoize the BFF status scenario we want to check against
	// so that our notifySuggestionResultChange effect does not run for all non-init statuses individually
	const bffHasInitialized = useMemo(() => {
		return status !== RequestStatus.INIT;
	}, [status]);

	// Notify consumer when the suggestion result changes
	// (if they provided an onResultChange function and the conditions for whether to use it are met).
	// We call this in a second effect rather than after the fetchUCSuggestions promise resolves, in case
	// the data or error state changes some other way.
	useEffect(
		function notifySuggestionResultChange() {
			// we should theoretically not need to check against shouldFetchSuggestions in this effect,
			// because if it's false the data/loading/error values should not change...as long as the BFF status is INIT.
			// If somehow the BFF status is not INIT, onResultChange would run if we didn't check this here.
			if (!shouldFetchSuggestions) {
				return;
			}

			if (onResultChange && bffHasInitialized) {
				onResultChange?.({ data, isLoading, hasError });
			}
		},
		[onResultChange, data, isLoading, hasError, bffHasInitialized, shouldFetchSuggestions],
	);

	const needFetchUser = useMemo(() => {
		return data?.items?.some((item) => {
			return REQUIRED_USER_ITEMS.includes(item.ati);
		});
	}, [data?.items]);

	useEffect(
		function fetchUserConditionally() {
			async function getUser() {
				if (fg('navx-2335-improving-uc-fetch-performance')) {
					// If consumer provided a user, use that
					if (currentUser) {
						setUser(currentUser);
						return;
					}
					// otherwise fetch user once if not already provided by currentUser
					const fetchedUser = await fetchUser();
					if (fetchedUser) {
						setUser(fetchedUser);
					}
				}
			}
			if (needFetchUser || shouldFetchUserV2) {
				getUser();
			}
		},
		[currentUser, fetchUser, needFetchUser, shouldFetchUserV2],
	);

	return fg('navx-2335-improving-uc-fetch-performance') ? (
		<UserContext.Provider value={{ user }}>{children}</UserContext.Provider>
	) : (
		children
	);
}

function FlagsWrapper({ children }: { children: ReactNode }) {
	if (fg('navx_uc_platformisation_forms_enabled')) {
		return <FlagsProvider>{children}</FlagsProvider>;
	}
	return children;
}

function AnalyticsWrapperComponent({ children }: { children: ReactNode }) {
	if (fg('navx-1690-analytics-context-wrapper')) {
		return (
			<NavigationAnalyticsContext data={{ source: 'topNav' }}>
				{children}
			</NavigationAnalyticsContext>
		);
	}
	return children;
}

function TopLevelWrappersInner({ children }: { children: ReactNode }) {
	// We need better error boundary, this is a temporary solution
	const AnalyticsWrapper =
		FeatureGates.initializeCalled() &&
		FeatureGates.getExperimentValue(
			'platform_fix_create_button_mutation_element',
			'isEnabled',
			false,
		)
			? AnalyticsWrapperComponent
			: ({ children }: { children: ReactNode }) => {
					if (fg('navx-1690-analytics-context-wrapper')) {
						return (
							<NavigationAnalyticsContext data={{ source: 'topNav' }}>
								{children}
							</NavigationAnalyticsContext>
						);
					}
					return children;
				};

	return (
		<PrefetchWrapper>
			<FlagsWrapper>
				<AnalyticsWrapper>{children}</AnalyticsWrapper>
			</FlagsWrapper>
		</PrefetchWrapper>
	);
}
/**
 * @description Wraps the children in a ErrorBoundary, BFFProvider, and PopupWrapperInner
 * Be careful to add any new wrappers that does asnyc requests or lazy loading components, which could break SSR or vc90s
 * @param children - The children to wrap
 * @returns
 */
export function TopLevelWrappers({ children }: { children: ReactNode }) {
	return (
		<ErrorBoundary fallback={null}>
			{/* BFF provider has to be in the upper level, otherwise it will not work */}
			<BFFProvider consumer={BFFConsumerEnum.UNIVERSAL_CREATE}>
				<TopLevelWrappersInner>{children}</TopLevelWrappersInner>
			</BFFProvider>
		</ErrorBoundary>
	);
}
